import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, MapPin, Menu, X } from "lucide-react";
import { SITE } from "../lib/site";

const links = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/insurance", label: "Auto Insurance" },
  { to: "/notary", label: "Notary" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <motion.header
        initial={{ y: -30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: [0.7, 0, 0.2, 1] }}
        className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
          scrolled ? "bg-[#0A192F]/95 backdrop-blur-xl" : "bg-[#0A192F]/40 backdrop-blur-md"
        }`}
        data-testid="site-nav"
      >
        <div className="max-w-[1500px] mx-auto px-6 lg:px-10 py-4 flex items-center justify-between text-white">
          <Link to="/" className="flex items-center gap-3 group min-w-0" data-testid="nav-logo">
            <span className="w-8 h-8 rounded-full border border-[#C5A059] flex items-center justify-center text-[#C5A059] font-serif text-lg shrink-0">
              R
            </span>
            <div className="leading-tight truncate">
              <div className="font-serif text-base sm:text-lg tracking-tight truncate">Rhawn Auto Tag</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-9 text-sm">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `link-under font-sans tracking-wide transition-colors ${
                    isActive ? "text-[#C5A059]" : "text-white/85 hover:text-white"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href={SITE.phoneHref}
              className="hidden lg:inline-flex items-center gap-2 text-sm text-[#C5A059] hover:text-white transition"
              data-testid="nav-call-btn"
            >
              <Phone className="w-4 h-4" /> {SITE.phone}
            </a>
            <Link
              to="/insurance"
              className="hidden lg:inline-flex btn-gold text-sm py-2.5 px-4"
              data-testid="nav-quote-btn"
            >
              Get Quote
            </Link>
            <button
              onClick={() => setOpen(true)}
              aria-label="Open menu"
              className="lg:hidden inline-flex w-10 h-10 items-center justify-center rounded-full border border-white/30"
              data-testid="nav-mobile-open"
            >
              <Menu className="w-5 h-5" />
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            key="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[70] bg-[#0A192F] text-white"
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between px-6 py-4">
              <span className="font-serif text-lg">Rhawn Auto Tag</span>
              <button
                onClick={() => setOpen(false)}
                aria-label="Close menu"
                className="w-10 h-10 flex items-center justify-center rounded-full border border-white/30"
                data-testid="nav-mobile-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 pt-16 flex flex-col gap-6">
              {links.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ y: 30, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.05 * i, duration: 0.6 }}
                >
                  <NavLink
                    to={l.to}
                    onClick={() => setOpen(false)}
                    className="font-serif text-4xl block"
                    data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              <div className="pt-10 flex flex-col gap-3">
                <a href={SITE.phoneHref} className="btn-gold justify-center" data-testid="mobile-call">
                  <Phone className="w-4 h-4" /> {SITE.phone}
                </a>
                <a
                  href={SITE.mapsUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-ghost justify-center text-white border-white/50"
                  data-testid="mobile-directions"
                >
                  <MapPin className="w-4 h-4" /> Get Directions
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
